import { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, Smartphone, Tv, Monitor, Apple, Box, Cast } from "lucide-react";
import Nav from "@/saas/components/Nav";
import Footer from "@/saas/components/Footer";
import SButton from "@/saas/components/SButton";
import { useI18n } from "@/saas/i18n";
import { PLATFORMS, detectPlatform } from "@/saas/components/platforms";
import { BRAND } from "@/saas/config/brand";

const ICON_MAP = {
  apple:   Apple,
  android: Smartphone,
  tv:      Tv,
  windows: Box,
  web:     Cast,
  mac:     Monitor,
};

export default function AppDownload() {
  const { t } = useI18n();
  const detected = useMemo(() => detectPlatform(), []);
  const detectedDef = PLATFORMS.find((p) => p.id === detected) || null;
  const qrValue = `https://${BRAND.domain}/app`;

  return (
    <div className="min-h-screen bg-bg text-white">
      <Nav />
      <main className="pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl tracking-tighter leading-[0.95] mb-4">
              {t("app.title")}
            </h1>
            <p className="text-lg text-white/60">{t("app.subtitle")}</p>
          </div>

          {/* Detected device highlight */}
          {detectedDef && (
            <div className="mb-10 p-6 rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/10 via-transparent to-transparent flex items-center gap-5 flex-wrap">
              <div className="w-14 h-14 rounded-xl bg-gold/15 border border-gold/40 grid place-items-center shrink-0">
                <PlatformIcon id={detectedDef.icon} className="w-6 h-6 text-gold" />
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="text-[11px] font-mono uppercase tracking-widest text-gold mb-1">{t("app.detected")}</div>
                <div className="font-display text-2xl font-bold">{t(detectedDef.labelKey)}</div>
                <div className="text-xs text-white/50 font-mono">{detectedDef.note}</div>
              </div>
              <SButton href={detectedDef.href} icon={Download} size="lg" data-testid="app-primary-download">
                {t("app.download")}
              </SButton>
            </div>
          )}

          {/* Platforms grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
            {PLATFORMS.map((p) => (
              <a
                key={p.id}
                href={p.href}
                className={`group relative p-5 rounded-xl border transition-all duration-200 ${
                  p.id === detected
                    ? "border-gold/40 bg-gold/5 hover:border-gold/70"
                    : "border-white/10 bg-surface/60 hover:border-white/30 hover:-translate-y-1"
                }`}
                data-testid={`app-platform-${p.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-lg bg-white/5 border border-white/10 grid place-items-center shrink-0 group-hover:bg-white/10 transition">
                    <PlatformIcon id={p.icon} className="w-5 h-5 text-white/80" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold">{t(p.labelKey)}</div>
                    <div className="text-[11px] font-mono text-white/40">{p.note}</div>
                  </div>
                  <Download className="w-4 h-4 text-white/40 group-hover:text-gold transition" />
                </div>
              </a>
            ))}
          </div>

          {/* QR + instructions */}
          <div className="grid md:grid-cols-[auto,1fr] gap-8 items-center p-8 rounded-2xl border border-white/10 bg-surface/60">
            <div className="p-4 rounded-xl bg-white">
              <QRCodeSVG value={qrValue} size={180} level="M" fgColor="#050505" bgColor="#ffffff" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold mb-2">{t("app.qr.title")}</h3>
              <p className="text-white/60 max-w-md">{t("app.qr.desc")}</p>
              <div className="mt-4 flex items-center gap-2 text-xs text-white/40 font-mono">
                <Cast className="w-3.5 h-3.5" />
                {qrValue}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function PlatformIcon({ id, className }) {
  const I = ICON_MAP[id] || Cast;
  return <I className={className} />;
}
